// components/VariantPreview.tsx
import React from "react";
import Typography from "@components/typography";
import theme from "@utils/theme";

interface VariantPreviewProps {
  selectedColors: any;
  selectedAttributes: any;
  isMobile: boolean;
  setSelectedAttributes: (attributes: any) => void;
  setSelectedColors: (colors: any) => void;
}

export const VariantPreview: React.FC<VariantPreviewProps> = ({
  selectedColors,
  selectedAttributes,
  isMobile,
  setSelectedAttributes,
  setSelectedColors,
}) => {
  if (selectedColors.length === 0 && selectedAttributes.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        border: "1px solid #eaeaea",
        borderRadius: "10px",
        padding: "16px",
        background: "#fafafa",
        marginBottom: "16px",
      }}
    >
      <Typography styleText="font-size:15px;font-weight:600;margin-bottom:8px">
        Selected Options
      </Typography>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "30px",
        }}
      >
        {/* Selected Colors */}
        {selectedColors.length > 0 && (
          <div>
            <Typography styleText="font-size:14px;font-weight:500;margin-bottom:6px">
              Colors:
            </Typography>
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              {selectedColors.map((color: any) => (
                <div
                  key={color.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    background: "#fff",
                    padding: "5px 10px",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: color.code,
                      border: "1px solid #ccc",
                    }}
                  />
                  <span style={{ fontSize: 13 }}>{color.name}</span>
                  <span
                    onClick={() => {
                      setSelectedColors((prev: any) =>
                        prev.filter((c: any) => c.id !== color.id)
                      );
                    }}
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      color: "#666",
                      fontWeight: "bold",
                    }}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Attributes */}
        {selectedAttributes.length > 0 && (
          <div>
            <Typography styleText="font-size:14px;font-weight:500;margin-bottom:6px">
              Attributes:
            </Typography>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "12px",
              }}
            >
              {selectedAttributes.map((attr: any) => (
                <AttributeItem
                  key={attr.id}
                  attr={attr}
                  onSelectAll={() => {
                    setSelectedAttributes((prev: any) =>
                      prev.map((a: any) =>
                        a.id === attr.id
                          ? {
                              ...a,
                              selectedValues: (a.values || []).slice(),
                            }
                          : a
                      )
                    );
                  }}
                  onClear={() => {
                    setSelectedAttributes((prev: any) =>
                      prev.map((a: any) =>
                        a.id === attr.id ? { ...a, selectedValues: [] } : a
                      )
                    );
                  }}
                  onRemove={() => {
                    setSelectedAttributes((prev: any) =>
                      prev.filter((a: any) => a.id !== attr.id)
                    );
                  }}
                  onToggleValue={(val) => {
                    setSelectedAttributes((prev: any) =>
                      prev.map((a: any) => {
                        if (a.id !== attr.id) return a;
                        const current: string[] = Array.isArray(
                          a.selectedValues
                        )
                          ? [...a.selectedValues]
                          : [];
                        const idx = current.indexOf(val);
                        if (idx > -1) current.splice(idx, 1);
                        else current.push(val);
                        return {
                          ...a,
                          selectedValues: current,
                        };
                      })
                    );
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AttributeItem: React.FC<{
  attr: any;
  onSelectAll: () => void;
  onClear: () => void;
  onRemove: () => void;
  onToggleValue: (val: string) => void;
}> = ({ attr, onSelectAll, onClear, onRemove, onToggleValue }) => {
  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: "6px",
        background: "#fff",
        padding: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Typography styleText="font-size:14px;font-weight:600">
          {attr.name}
        </Typography>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={onSelectAll}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
              color: "#0070f3",
            }}
          >
            Select all
          </button>
          <button
            type="button"
            onClick={onClear}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
              color: "#ff4d4f",
            }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onRemove}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
              color: "#666",
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {(attr.values || []).map((val: any) => {
          const isSelected =
            Array.isArray(attr.selectedValues) &&
            attr.selectedValues.includes(val);
          return (
            <button
              key={val}
              type="button"
              onClick={() => onToggleValue(val)}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: isSelected
                  ? `1px solid ${theme.colors.primary}`
                  : "1px solid #ddd",
                background: isSelected ? `${theme.colors.primary}20` : "#fff",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              {val}
            </button>
          );
        })}
      </div>

      {attr.selectedValues && attr.selectedValues.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <small style={{ color: "#555" }}>
            Selected: {attr.selectedValues.join(", ")}
          </small>
        </div>
      )}
    </div>
  );
};
